<template>
    <v-layout row wrap>
        <!-- Header -->
        <v-flex md12 class="mb-3">
            <h2 class="title font-weight-medium" style="color: var(--bindez-green-color);">
                Connect Your Own Profiles
            </h2>
        </v-flex>

        <!-- Connect Button -->
        <v-flex md12 class="mb-4">
            <v-btn
                color="bindezcolor"
                depressed
                dark
                @click="handleConnect"
                :loading="connecting"
            >
                <v-icon left>fab fa-facebook</v-icon>
                <span class="text-capitalize font-weight-regular subheading">Connect with Facebook</span>
            </v-btn>
        </v-flex>

        <!-- Connected Pages List -->
        <v-flex md12>
            <div class="overline grey--text mb-2">OWN PROFILES</div>
            <v-divider class="mb-3"></v-divider>

            <div v-if="loading" class="text-xs-center py-4">
                <v-progress-circular indeterminate color="bindezcolor"></v-progress-circular>
            </div>

            <div v-else-if="integrations.length === 0" class="grey--text body-1 py-3">
                No pages connected yet. Click "Connect with Facebook" to get started.
            </div>

            <v-layout
                v-else
                v-for="page in integrations"
                :key="page.facebook_page_id"
                row
                align-center
                class="page-row py-2"
            >
                <v-flex shrink class="mr-3">
                    <v-avatar size="48">
                        <img
                            :src="page.picture_url || `https://graph.facebook.com/${page.facebook_page_id}/picture?type=square`"
                            :alt="page.facebook_page_name"
                        />
                    </v-avatar>
                </v-flex>
                <v-flex grow>
                    <div class="subheading font-weight-medium">{{ page.facebook_page_name }}</div>
                    <div class="caption grey--text">
                        <v-icon small color="blue darken-2" class="mr-1">fab fa-facebook</v-icon>
                        {{ formatFanCount(page.fan_count) }} fans
                    </div>
                    <div v-if="isTokenExpired(page)" class="caption error--text mt-1">
                        <v-icon small color="error" class="mr-1">warning</v-icon>
                        Token expired. Please reconnect with Facebook.
                    </div>
                </v-flex>
                <v-flex shrink>
                    <v-btn
                        flat
                        small
                        color="bindezcolor"
                        dark
                        :disabled="page.connected"
                        @click="handleAddPage(page.facebook_page_id)"
                        :loading="addingPage === page.facebook_page_id"
                    >
                        Add
                    </v-btn>
                    <v-btn
                        flat
                        small
                        color="error"
                        @click="handleDisconnect(page.facebook_page_id)"
                        :loading="disconnecting === page.facebook_page_id"
                    >
                        Remove
                    </v-btn>
                </v-flex>
            </v-layout>
        </v-flex>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="4000">
            {{ snackbar.message }}
            <v-btn flat @click="snackbar.show = false">Close</v-btn>
        </v-snackbar>
    </v-layout>
</template>

<script>
/*eslint-disable*/
import { mapState } from 'vuex';

export default {
    name: 'FacebookConnectSection',

    data() {
        return {
            loading: false,
            connecting: false,
            disconnecting: null,
            addingPage: null,
            snackbar: {
                show: false,
                message: '',
                color: 'success'
            }
        };
    },

    computed: {
        ...mapState('facebook', ['integrations']),
        monitorId() {
            return this.$route.params.monitor_uuid;
        }
    },

    mounted() {
        this.handleQueryParam();
        this.loadIntegrations();
    },

    methods: {
        handleQueryParam() {
            const { facebook, reason } = this.$route.query;
            if (!facebook) return;

            if (facebook === 'success') {
                this.showSnackbar('Facebook page connected successfully.', 'success');
            } else if (facebook === 'error') {
                const messages = {
                    user_denied: 'Connection was cancelled.',
                    invalid_state: 'Connection failed due to an invalid session. Please try again.',
                    no_pages: 'No Facebook pages found for your account.',
                    token_exchange_failed: 'Connection failed. Please try again.'
                };
                this.showSnackbar(messages[reason] || 'Connection failed. Please try again.', 'error');
            }

            const url = new URL(window.location.href);
            url.searchParams.delete('facebook');
            url.searchParams.delete('reason');
            history.replaceState(null, '', url.toString());
        },

        loadIntegrations() {
            this.loading = true;
            this.$store.dispatch('facebook/fetchIntegrations', this.monitorId)
                .finally(() => { this.loading = false; });
        },

        handleConnect() {
            this.connecting = true;
            let returnTo = encodeURIComponent(this.$route.fullPath);
            this.$store.dispatch('facebook/getConnectUrl', { monitorId: this.monitorId, returnTo })
                .then(({ url }) => { window.location.href = url; })
                .catch(() => {
                    this.showSnackbar('Failed to start Facebook connection. Please try again.', 'error');
                    this.connecting = false;
                });
        },

        handleAddPage(pageId) {
            this.addingPage = pageId;
            this.$store.dispatch('facebook/connectPage', { monitorId: this.monitorId, pageId })
                .then(() => {
                    this.showSnackbar('Page added successfully.', 'success');
                    this.loadIntegrations();
                })
                .catch(() => this.showSnackbar('Failed to add page. Please try again.', 'error'))
                .finally(() => { this.addingPage = null; });
        },

        handleDisconnect(pageId) {
            this.disconnecting = pageId;
            this.$store.dispatch('facebook/disconnect', { monitorId: this.monitorId, pageId })
                .then(() => {
                    this.showSnackbar('Page removed successfully.', 'success');
                    this.loadIntegrations();
                })
                .catch(() => this.showSnackbar('Failed to remove page. Please try again.', 'error'))
                .finally(() => { this.disconnecting = null; });
        },

        isTokenExpired(page) {
            if (!page.long_lived_token_expires_at) return false;
            return new Date(page.long_lived_token_expires_at) < new Date();
        },

        formatFanCount(count) {
            if (!count && count !== 0) return '—';
            if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
            if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
            return count.toLocaleString();
        },

        showSnackbar(message, color) {
            this.snackbar = { show: true, message, color };
        }
    }
};
</script>

<style scoped>
.page-row {
    border-bottom: 1px solid #f0f0f0;
}
.page-row:last-child {
    border-bottom: none;
}
</style>
